import { z } from 'zod';
/**
 * Scheduling-owned duplicated consumer contract for the Projects-owned
 * `projects.project-updated.v2` fact. Scheduling deliberately does not import
 * the Projects event implementation: this copy is proven equal to the
 * producer contract by an executable apps/api compatibility test, so physical
 * independence survives while drift still fails loudly. An unknown changed
 * field or snapshot key is an incompatible payload and must dead-letter as a
 * permanent delivery error, never be guessed around.
 */
export const schedulingProjectUpdatedConsumerEventName = 'projects.project-updated.v2';
/**
 * Replay identity: tenant + source event ID + this subscriber contract.
 */
export const schedulingProjectUpdatedSubscriberContract = 'scheduling.subscribers.project-updated.v2';
export const schedulingEventLocationSynchronizedEventName = 'scheduling.event-location-synchronized.v1';
const schedulingConsumedChangedFieldSchema = z.enum([
    'addressLine1',
    'addressLine2',
    'city',
    'countryCode',
    'customerAddressLine1',
    'customerAddressLine2',
    'customerCity',
    'customerCountryCode',
    'customerDisplayName',
    'customerEmail',
    'customerPhone',
    'customerPostalCode',
    'customerRegion',
    'isWaterJob',
    'name',
    'organizationId',
    'postalCode',
    'primaryContactId',
    'priority',
    'projectType',
    'propertyType',
    'region',
    'startDate',
    'status',
    'targetCompletionDate',
]);
const schedulingConsumedServiceLocationSchema = z.strictObject({
    addressLine1: z.string().max(240).nullable(),
    addressLine2: z.string().max(240).nullable(),
    city: z.string().max(120).nullable(),
    region: z.string().max(120).nullable(),
    postalCode: z.string().max(40).nullable(),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
});
const schedulingConsumedProjectSnapshotSchema = z.strictObject({
    id: z.string().uuid(),
    projectNumber: z.string().min(1).max(80),
    name: z.string().min(1).max(240),
    status: z.string().min(1).max(80),
    projectType: z.string().min(1).max(80),
    priority: z.enum(['low', 'normal', 'high', 'emergency']),
    propertyType: z.string().min(1).max(80).nullable(),
    isWaterJob: z.boolean(),
    startDate: z.string().date().nullable(),
    targetCompletionDate: z.string().date().nullable(),
    serviceLocation: schedulingConsumedServiceLocationSchema,
});
export const schedulingProjectUpdatedConsumerPayloadSchema = z.strictObject({
    projectId: z.string().uuid(),
    revision: z.number().int().positive(),
    changedFields: z.array(schedulingConsumedChangedFieldSchema).min(1).max(schedulingConsumedChangedFieldSchema.options.length),
    previous: schedulingConsumedProjectSnapshotSchema,
    project: schedulingConsumedProjectSnapshotSchema,
});
/**
 * The service-location fields whose change makes an event-address
 * synchronization eligible; the original reacted only to street, city,
 * state, and postal changes.
 */
export const schedulingAddressSyncChangedFields = [
    'addressLine1', 'city', 'region', 'postalCode',
];
/**
 * The dedicated replay job whose worker provenance is the ONLY identity the
 * restricted SQL reconciliation `scheduling.project_address_sync_replay_v1`
 * accepts. The manifest's declared reconciliation procedure name maps to that
 * executable function.
 */
export const schedulingProjectAddressSyncReplayJobId = 'scheduling.jobs.project-address-sync-replay';
export const schedulingProjectAddressSyncReplayProcedure = 'scheduling.project-address-sync-replay.v1';
//# sourceMappingURL=scheduling-project-updated-consumer.contract.js.map